import { IllustrationSerializedChildren } from '@editor/fabric/shapes/Illustration/types';
import { getDeviceId } from '@editor/stores/userStore/utils';
import { BookmarkElementType, FontBookmark } from '@editor/types/Bookmarks';
import { Invite } from '@editor/types/CollaborationInvite';
import { ElementType } from '@editor/types/events';
import { apiFetchClient, KittlHeaderNames } from '@repo/api-client';
import { JSONValue } from '@repo/domain';
import { ResourceSharingAccess } from '@repo/domain/design';
import {
  MinimalUserProfileWithId,
  SubscriptionPlan,
  User,
} from '@repo/domain/user';
import { USER_ACCESS_TOKEN_KEY } from '@repo/transport/token';
import Cookies from 'js-cookie';

import type { ErasePathObject } from '../fabric/mixins/erasable/types';
import { getHasAllObjectsRemoved } from '../helpers/hasAllObjectsRemoved';
import monitoring from '../services/monitoring';
import { useUserStore } from '../stores/userStore';
import type {
  Challenge,
  Design,
  DesignStateMultiBoard,
  GenericApiResponse,
  Notification,
  Post,
  PoWChallengeResponse,
  PoWChallenge,
  ResponseWithError,
  Snapshot,
  Template,
  BasicUserInfoResponse,
  DesignState,
  UserCreditBalanceResponse,
  UnsplashPhotoRaw,
} from '../types';
import { UtmData } from '../types/UtmData';
import { apiFetchRetry, apiTryFetchRetry } from '../utils/apiFetchRetry';
import {
  fetchRetry,
  OfflineError,
  retryOnStatusCode,
} from '../utils/fetchRetry';

import { FreepikSearchResult } from './cmsContent';
import { USER_API_URL } from './constants';
import { getAssetWorkspaceHeaders } from './utils';

type ToggleGeneratedContentPublicModeResponse = GenericApiResponse<{
  generatedContentPublicMode: boolean;
}>;

type ActionsResponse = GenericApiResponse<{
  results: { id: string; createdAt: string }[];
}>;

type DeleteActionResponse = GenericApiResponse<{
  results: string[];
}>;

interface CreateUploadResponse {
  results: {
    id: string;
    objectName: string;
    mimeType: string;
    size: number;
  }[];
}

export type GetBookmarksResult =
  | Template[]
  | ElementType[]
  | FreepikSearchResult[]
  | UnsplashPhotoRaw[]
  | FontBookmark[];
/**
 * get the current user id
 */
export const getUserId = (): string | undefined => {
  const user = useUserStore.getState()?.user;
  if (user) {
    return user.id;
  }
  return undefined;
};
/**
 * get the current profile accessToken
 */
export const getAccessToken = (): string | undefined => {
  return useUserStore.getState().getProfileToken();
};

/**
 * get the current access accessToken
 */
export const getUserToken = (): string | undefined => {
  return Cookies.get(USER_ACCESS_TOKEN_KEY);
};

class MissingObjectsError extends Error {
  constructor() {
    super('Design update removed all objects');
    this.name = 'MissingObjectsError';
  }
}

export const isMissingObjectsError = (error: unknown): boolean => {
  return error instanceof Error && error.name === 'MissingObjectsError';
};

const uploadToS3 = async (
  url: string,
  blob: Blob,
  type: string
): Promise<void> => {
  const uploadResponse = await fetchRetry(url, {
    method: 'PUT',
    body: blob,
    headers: { 'Content-Type': type },
  });
  if (!uploadResponse.ok) {
    throw new Error(
      `Failed to upload object. ${uploadResponse.statusText} (${uploadResponse.status})`
    );
  }
};

const DEFAULT_MOCK_EXPIRATION_DAYS = 1;

export const uploadObject = async (
  file: string | File | Blob,
  type = 'image/jpeg'
): Promise<string> => {
  try {
    let blob: Blob;
    if (typeof file === 'string') {
      blob = await (await fetchRetry(file)).blob();
    } else {
      blob = file;
    }

    const resp = await apiTryFetchRetry<{ url: string; objectId: string }>(
      `${USER_API_URL}/users/create_signed_url`,
      { method: 'POST' }
    );

    if ('error' in resp || !resp.url || !resp.objectId) {
      // either connections issues or user is not authenticated anymore
      throw new Error(
        `Failed to create signed url. ${'error' in resp ? resp.error : ''}`
      );
    }

    await uploadToS3(resp.url, blob, type);

    return resp.objectId as string;
  } catch (error) {
    monitoring.captureException(error);
    throw error;
  }
};

const uploadObjects = async (
  files: { file: Blob; mime?: string }[],
  onObjectUploaded?: () => void
): Promise<string[]> => {
  try {
    const resp = await apiTryFetchRetry<
      GenericApiResponse<{ results: { objectId: string; url: string }[] }>
    >(`${USER_API_URL}/users/create_signed_url`, {
      method: 'POST',
      body: JSON.stringify({
        count: files.length,
      }),
    });

    if (!resp.success || resp.results.length !== files.length) {
      // either connections issues or user is not authenticated anymore
      throw new Error(`Failed to create signed url. ${resp.error}`);
    }

    let signedURLIndex = 0;
    for (const file of files) {
      await uploadToS3(
        resp.results[signedURLIndex++].url,
        file.file,
        file.mime ? file.mime : file.file.type
      );

      onObjectUploaded && onObjectUploaded();
    }

    return resp.results.map(
      (r: { objectId: string; url: string }) => r.objectId
    );
  } catch (error) {
    monitoring.captureException(error);
    throw error;
  }
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const createAPI = ({ url }: { url: string }) => {
  const hasAllObjectsRemoved = getHasAllObjectsRemoved();
  const getUserInfo = async (
    accessToken: string
  ): Promise<User | ResponseWithError> => {
    const response = await apiTryFetchRetry<User | ResponseWithError>(
      `${url}/users/me`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'x-device-id': getDeviceId(),
        },
      }
    );

    if ((response as ResponseWithError).error) {
      return { error: (response as ResponseWithError).error };
    } else {
      const result = response as User;
      /* Temporary until we solve the whole auth flow */
      if (!result.name && result.email) {
        result.name = result.email.split('@')[0];
      }

      return result;
    }
  };

  const getBasicUserInfo = async (
    accessToken: string
  ): Promise<GenericApiResponse<BasicUserInfoResponse>> => {
    return await apiTryFetchRetry(`${url}/users/basic`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  };

  const getUserCreditInfo = async (
    _: string
  ): Promise<GenericApiResponse<UserCreditBalanceResponse>> => {
    // @TODO by https://app.clickup.com/t/86c2ztc52:
    // Create a proper openapi docs for this endpoint
    // Rename to be not based on the user
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    return (await apiFetchClient.GET('/users/balance'))?.data;
  };

  interface ExchangeTemporaryTokenResponse {
    accessToken: string;
    name: string;
    id: string;
    isSignup: boolean;
    promoCode: string;
    referral: string;
  }

  // exchange a temporary token obtained from a 3rd party authentication for a permanent one.
  const exchangeTemporaryToken = async (
    token: string
  ): Promise<ExchangeTemporaryTokenResponse | ResponseWithError> => {
    return await apiTryFetchRetry<ExchangeTemporaryTokenResponse>(
      `${USER_API_URL}/sessions/exchange`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: encodeURI(`exchangeToken=${token}`),
      }
    );
  };

  const createSession = async (
    email: string,
    password: string,
    teamInviteId?: string,
    powChallenge?: PoWChallenge,
    otpCode?: string,
    otpExchangeToken?: string
  ): Promise<
    | (User & { accessToken: string })
    | { exchangeToken: string }
    | ResponseWithError
  > => {
    return await apiTryFetchRetry<
      (User & { accessToken: string }) | { exchangeToken: string }
    >(`${url}/sessions/create`, {
      method: 'POST',
      headers: {
        'x-challenge-nonce': powChallenge?.nonce ?? '',
        'x-challenge-solution': powChallenge?.solution?.toString() ?? '',
      },
      body: JSON.stringify({
        email,
        password,
        teamInviteId,
        ...(otpCode && { otpCode }),
        ...(otpExchangeToken && { otpExchangeToken }),
      }),
    });
  };

  // Create a new team session
  const createTeamSession = async (
    team: string
  ): Promise<
    (User & { success: boolean; token: string }) | ResponseWithError
  > => {
    return await apiTryFetchRetry(`${url}/sessions/team/create`, {
      method: 'POST',
      body: JSON.stringify({ team }),
    });
  };

  const createUser = async ({
    name,
    email,
    password,
    marketingOptIn = false,
    invite,
    referral,
    promoCode,
    impactClickId,
    adClickId,
    utm,
    teamInviteId,
    signupUrl,
    powChallenge,
    deviceScreenSizeType,
  }: {
    name: string;
    email: string;
    password: string;
    marketingOptIn?: boolean;
    invite: string;
    referral: string;
    promoCode: string;
    impactClickId: string;
    adClickId?: string;
    utm?: UtmData;
    teamInviteId?: string;
    signupUrl: string;
    deviceScreenSizeType?: string;
    powChallenge?: PoWChallenge;
  }): Promise<
    {
      success: boolean;
    } & (
      | {
          accessToken: string;
          id: string;
        }
      | ResponseWithError
    )
  > => {
    const optional = {
      marketingOptIn,
      invite,
      referral,
      promoCode,
      impactClickId,
      adClickId,
      utm,
      teamInviteId,
      signupUrl,
      deviceScreenSizeType,
    };

    return await apiTryFetchRetry(`${url}/users/create`, {
      method: 'POST',
      headers: {
        'x-challenge-nonce': powChallenge?.nonce ?? '',
        'x-challenge-solution': powChallenge?.solution?.toString() ?? '',
      },
      body: JSON.stringify({
        name,
        email,
        password,
        ...optional,
      }),
    });
  };

  const deleteSession = async (): Promise<ResponseWithError> => {
    return await apiTryFetchRetry(`${url}/sessions/delete`, {
      method: 'POST',
    });
  };

  const requestPasswordResetCode = async (
    email: string,
    powChallenge?: PoWChallenge | null
  ): Promise<GenericApiResponse> => {
    return await apiTryFetchRetry(`${url}/users/send_recovery_code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'x-challenge-nonce': powChallenge?.nonce ?? '',
        'x-challenge-solution': powChallenge?.solution?.toString() ?? '',
      },
      body: `email=${encodeURIComponent(email)}`,
    });
  };

  // Update onboarding state when user follows flow
  const updateOnboardingState = async (step: {
    name: string;
  }): Promise<User | { error: string }> => {
    const data = new FormData();
    data.append('onboardingState', step.name);

    return await apiTryFetchRetry(`${url}/users/update`, {
      method: 'POST',
      body: data,
    });
  };

  const updateUserEmail = async (payload: {
    email: string;
  }): Promise<User | { error: string }> => {
    const data = new FormData();
    data.append('email', payload.email);

    return await apiTryFetchRetry(`${url}/users/update`, {
      method: 'POST',
      body: data,
    });
  };

  // get the uploaded elements from a user
  const getUploadedElements = async (
    take = 10,
    cursor = null,
    folderId = null
  ): Promise<
    | {
        results: { id: string; objectName: string }[];
        skip?: number;
        take?: number;
        cursor?: string;
        nextCursor?: string;
      }
    | { success: false; error: string }
  > => {
    const result = await apiTryFetchRetry<{
      results: { id: string; objectName: string }[];
      skip?: number;
      take?: number;
      cursor?: string;
      nextCursor?: string;
    }>(
      `${url}/uploads/index?take=${take}${
        folderId ? `&folderId=${folderId}` : ''
      }${(cursor && `&skip=1&cursor=${cursor}`) || ''}`,
      {
        method: 'GET',
        headers: getAssetWorkspaceHeaders(),
      }
    );

    // in case uploads are fetched with no user logged in, just return an empty array
    if ('error' in result) {
      return { results: [] };
    }

    return result;
  };

  // upload a new file
  const uploadElement = async (
    file: File,
    isMockup?: boolean,
    isTemporaryFile?: boolean
  ): Promise<
    | {
        id?: string;
        objectName?: string;
        folderId?: string;
      }
    | { success: false; error: string }
  > => {
    let objectId: string;
    try {
      objectId = await uploadObject(file, file.type);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      return { success: false, error: errorMessage };
    }

    const body: {
      objectId: string;
      isMockup?: boolean;
      daysToExpire?: number;
    } = {
      objectId,
      isMockup,
    };

    if (isTemporaryFile) {
      body.daysToExpire = DEFAULT_MOCK_EXPIRATION_DAYS;
    }

    return await apiTryFetchRetry(`${url}/uploads/create`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: getAssetWorkspaceHeaders(),
    });
  };

  // upload new files
  const uploadElements = async (
    files: { file: Blob; mime?: string }[],
    onObjectUploaded?: () => void
  ): Promise<
    GenericApiResponse<{
      results?: { id: string; objectName: string; mimeType: string }[];
    }>
  > => {
    let objectIds: string[];
    try {
      objectIds = await uploadObjects(files, onObjectUploaded);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      return { success: false, error: errorMessage };
    }
    return await apiTryFetchRetry(`${url}/uploads/create`, {
      method: 'POST',
      body: JSON.stringify({
        objectIds,
      }),
      headers: getAssetWorkspaceHeaders(),
    });
  };

  // delete an uploaded file by id
  const deleteUpload = async (id: string): Promise<GenericApiResponse> => {
    return await apiTryFetchRetry(`${url}/uploads/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        ...getAssetWorkspaceHeaders(),
      },
      body: encodeURI(`id=${id}`),
    });
  };

  // delete multiple uploaded files by their ids
  const deleteUploads = async (ids: string[]): Promise<GenericApiResponse> => {
    return await apiTryFetchRetry(`${url}/uploads/delete_many`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAssetWorkspaceHeaders(),
      },
      body: JSON.stringify({
        ids,
      }),
    });
  };

  const getDesign = async (
    id: string,
    signal?: AbortSignal
  ): Promise<null | { error: string } | Design> => {
    try {
      return await apiTryFetchRetry<Design>(`${url}/designs/${id}`, {
        method: 'GET',
        signal,
        cache: 'no-store',
      });
    } catch (e) {
      if (e instanceof OfflineError) {
        throw e;
      }
      return null;
    }
  };

  const getDesigns = async (
    take: number,
    skip: number,
    folderId?: string,
    sort?: string,
    workspaceId?: string
  ): Promise<{
    results: Array<Design>;
    total?: number;
    skip?: number;
    take?: number;
  }> => {
    const headers: Record<string, string> = {};
    if (workspaceId) {
      headers[KittlHeaderNames.WORKSPACE_ID] = workspaceId;
    }

    const result = await apiTryFetchRetry<{
      results: Array<Design>;
      total?: number;
      skip?: number;
      take?: number;
    }>(
      `${url}/designs/index?take=${take}&skip=${skip}${
        folderId ? `&folderId=${folderId}` : ''
      }${sort ? `&sort=${sort}` : ''}`,
      {
        method: 'GET',
        headers,
      }
    );

    if ('error' in result) {
      return { results: [] };
    }

    return result;
  };

  const getDesignOwner = async (
    id: string
  ): Promise<MinimalUserProfileWithId> => {
    const result = await apiTryFetchRetry<
      GenericApiResponse<{ user: MinimalUserProfileWithId }>
    >(`${url}/designs/${id}/owner`, {
      method: 'GET',
    });

    if ('error' in result) {
      return { id: '', name: '', profileImage: '', slug: '' };
    }

    return (result as { user: MinimalUserProfileWithId }).user;
  };

  const getDesignsWithoutFolders = async (options: {
    take: number;
    skip: number;
    folderId?: string;
    sort?: string;
    workspaceId?: string;
  }): Promise<{
    results: Array<Design>;
    total?: number;
    skip?: number;
    take?: number;
  }> => {
    const { take, skip, folderId, sort, workspaceId } = options;

    const headers: Record<string, string> = {};
    if (workspaceId) {
      headers[KittlHeaderNames.WORKSPACE_ID] = workspaceId;
    }

    const result = await apiTryFetchRetry<{
      results: Array<Design>;
      total: number;
      skip: number;
      take: number;
    }>(
      `${url}/designs/index?take=${take}&skip=${skip}${
        folderId ? `&folderId=${folderId}` : ''
      }${sort ? `&sort=${sort}` : ''}`,
      {
        method: 'GET',
        headers,
      }
    );

    if ('error' in result) {
      return { results: [] };
    }

    return result;
  };

  const getLockedDesigns = async (): Promise<{
    results: Design[];
  }> => {
    const result = await apiTryFetchRetry<{
      results: Design[];
    }>(`${url}/designs/index/locked`, {
      method: 'GET',
    });

    if ('error' in result) {
      return { results: [] };
    }

    return result;
  };

  // add a new design to the api
  const createDesign = async (
    name: string,
    state: DesignStateMultiBoard,
    preview: string | File | Blob,
    optionals: {
      workspaceId?: string;
      folderId?: string;
      deviceScreenSizeType?: string;
    }
  ): Promise<{ error: string } | Design> => {
    const { folderId, deviceScreenSizeType, workspaceId } = optionals;
    const data = new FormData();
    data.append('name', name);
    data.append('state', JSON.stringify(state));
    folderId && data.append('folderId', folderId);
    deviceScreenSizeType &&
      data.append('deviceScreenSizeType', deviceScreenSizeType);

    try {
      const objectId = await uploadObject(preview);
      data.append('objectId', objectId);
    } catch (_error) {
      return { error: 'Failed to upload project preview' };
    }

    const headers: Record<string, string> = {};
    if (workspaceId) {
      headers[KittlHeaderNames.WORKSPACE_ID] = workspaceId;
    }
    return await apiTryFetchRetry(`${url}/designs/create`, {
      method: 'POST',
      body: data,
      headers,
    });
  };

  // updates an existing design
  const updateDesign = async (
    id: string,
    patch: {
      name?: string;
      state?: DesignStateMultiBoard;
      preview?: string;
      sharing?: ResourceSharingAccess;
    }
  ): Promise<{ error: string } | Design> => {
    const { name, state, preview, sharing } = patch;

    const data = new FormData();
    data.append('id', id);
    const {
      allObjectsRemoved,
      previousObjectsCount,
      artboardCount,
      previousArtboardCount,
    } = hasAllObjectsRemoved(id, state);
    if (allObjectsRemoved) {
      // NOTE: values here are arbitrary and can be adjusted based on the actual data
      // the idea is to throw an error if its likely an undesired state
      const showError =
        artboardCount === previousArtboardCount &&
        artboardCount > 2 &&
        previousObjectsCount > 5;
      const error = new MissingObjectsError();
      monitoring.setTags({
        designId: id,
        // objectCount and artboardCount are added here to check whether we need to adjust the values
        objectCount: previousObjectsCount,
        artboardCount,
        showError,
      });

      // if the state change is likely undesired, throw an error and display a dialog to handle it
      // see above on how this is determined
      if (showError) {
        monitoring.captureException(error, {
          designId: id,
          previousObjectsCount,
        });

        throw error;
      }
    }
    if (name) {
      data.append('name', name);
    }
    if (state) {
      data.append('state', JSON.stringify(state));
    }
    if (preview) {
      try {
        const objectId = await uploadObject(preview);
        data.append('objectId', objectId);
      } catch (_error) {
        // if creating preview fails, we can update a design without a preview
      }
    }
    if (sharing) {
      data.append('sharing', sharing);
    }

    return await apiTryFetchRetry(`${url}/designs/update`, {
      method: 'POST',
      body: data,
    });
  };

  const deleteDesign = async (id: string): Promise<GenericApiResponse> => {
    return await apiTryFetchRetry(`${url}/designs/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: encodeURI(`id=${id}`),
    });
  };

  const duplicateDesign = async (
    id: string,
    workspaceId: string
  ): Promise<{ error: string } | Design> => {
    return await apiTryFetchRetry(`${url}/designs/duplicate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        [KittlHeaderNames.WORKSPACE_ID]: workspaceId,
      },
      body: encodeURI(`id=${id}`),
    });
  };

  const lockDesign = async (id: string): Promise<GenericApiResponse> => {
    return await apiTryFetchRetry(`${url}/designs/lock`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: encodeURI(`id=${id}`),
    });
  };

  const unlockDesign = async (id: string): Promise<GenericApiResponse> => {
    return await apiTryFetchRetry(`${url}/designs/unlock`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      keepalive: true,
      body: encodeURI(`id=${id}`),
    });
  };

  const removeAiImageDesign = async (
    id: string
  ): Promise<GenericApiResponse> => {
    return await apiTryFetchRetry(`${url}/designs/ai_image/remove`, {
      method: 'POST',
      body: JSON.stringify({
        id,
      }),
    });
  };

  const getShare = async (
    id: string
  ): Promise<{ error: string } | Snapshot> => {
    return await apiTryFetchRetry(`${url}/share/${id}`, {
      method: 'GET',
    });
  };

  // Gets an SSO token for logging in to Canny
  const getSSOToken = async (): Promise<string> => {
    const resp = await apiFetchRetry(`${url}/users/sso_token`, {
      method: 'GET',
    });
    const ssoTokenResponse = await resp.json();

    return ssoTokenResponse.token;
  };

  const getBookmarkReferences = async (): Promise<
    | { templates: string[]; elements: string[] }
    | { success: false; error: string }
  > => {
    return await apiTryFetchRetry(`${url}/bookmarks/references`, {
      method: 'GET',
    });
  };

  // get bookmarks from a user
  const getBookmarks = async (
    take = 10,
    skip = 0,
    type = 'TEMPLATE'
  ): Promise<
    | {
        cursor: string;
        nextCursor: string;
        results: GetBookmarksResult;
        skip: number;
        take: number;
        total: number;
      }
    | { success: false; error: string }
  > => {
    return await apiTryFetchRetry(
      `${url}/bookmarks/index?take=${take}&skip=${skip}&bookmarkType=${type}`,
      {
        method: 'GET',
      }
    );
  };

  const createBookmark = async (
    id: string,
    type = 'TEMPLATE'
  ): Promise<
    GenericApiResponse<{
      bookmark: {
        id: number;
        bookmarkType: keyof typeof BookmarkElementType;
        referenceId: string;
      };
    }>
  > => {
    return await apiTryFetchRetry(`${url}/bookmarks/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: encodeURI(`id=${id}&bookmarkType=${type}`),
    });
  };

  const deleteBookmark = async (
    id: string,
    type = 'TEMPLATE'
  ): Promise<GenericApiResponse> => {
    return await apiTryFetchRetry(`${url}/bookmarks/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: encodeURI(`id=${id}&bookmarkType=${type}`),
    });
  };

  // Notifications
  const getNotifications = async (
    take: number,
    skip: number
  ): Promise<
    | { results: Notification[]; skip: number; take: number }
    | { success: false; error: string }
  > => {
    return await apiTryFetchRetry(
      `${url}/notifications/index?take=${take}&skip=${skip}`,
      {
        method: 'GET',
      }
    );
  };

  const createCmsNotification = async (): Promise<
    | {
        success: boolean;
        created: boolean;
      }
    | { success: false; error: string }
  > => {
    return await apiTryFetchRetry(`${url}/notifications/create_cms`, {
      method: 'POST',
    });
  };

  const markNotificationRead = async (
    id: string
  ): Promise<{ success: boolean; error?: string }> => {
    return await apiTryFetchRetry(`${url}/notifications/mark_as_read`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: encodeURI(`notificationId=${id}`),
    });
  };

  const markNotificationsSeen = async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    return await apiTryFetchRetry(`${url}/notifications/mark_as_seen`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  };

  const changeSubscription = async (
    id: string,
    plan: SubscriptionPlan,
    days: number
  ): Promise<{ success: boolean }> => {
    return await apiTryFetchRetry(`${url}/subscription/admin/change`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: encodeURI(`id=${id}&plan=${plan}&days=${days}`),
    });
  };

  const createPost = async (
    category: string | undefined,
    useCases: string[],
    description: string,
    name: string,
    template: DesignStateMultiBoard,
    preview: string,
    tags: string[],
    challengeId: string,
    premium: boolean
  ): Promise<
    | {
        id?: string;
        challenge?: { slug: string };
        error?: string;
      }
    | Post
  > => {
    return await apiTryFetchRetry(`${url}/posts/create`, {
      method: 'POST',
      body: JSON.stringify({
        category,
        useCases,
        description,
        name,
        template: template,
        preview,
        tags,
        challengeId,
        premium,
      }),
    });
  };

  const updatePost = async (
    id: string,
    useCases: string[],
    description: string,
    name: string,
    state: DesignStateMultiBoard,
    preview: string,
    tags: string[],
    premium: boolean
  ): Promise<{ error: string } | Post> => {
    return await apiTryFetchRetry(`${url}/posts/update`, {
      method: 'POST',
      body: JSON.stringify({
        useCases,
        description,
        name,
        state,
        preview,
        templateId: id,
        tags,
        premium,
      }),
    });
  };

  const findUser = async (
    query = ''
  ): Promise<
    { results: User[] } | { success: false; error: string } | null
  > => {
    return await apiTryFetchRetry<
      { results: User[] } | { success: false; error: string } | null
    >(
      `${url}/users/find/?query=${query}&compact=true`,
      {
        method: 'GET',
        retryOn: retryOnStatusCode([503, 504]),
      },
      false
    );
  };

  const shareDesign = async ({
    designId,
    state,
    preview,
    editable,
    email,
    user,
  }: {
    designId?: string;
    state?: DesignState;
    preview: string;
    editable: boolean;
    email?: string;
    user?: string;
  }): Promise<{ error: string } | Snapshot> => {
    const objectId = await uploadObject(preview, 'image/png');

    const body = new FormData();
    if (designId) {
      body.append('designId', designId);
    }
    if (state) {
      body.append('state', JSON.stringify(state));
    }
    body.append('objectId', objectId);
    body.append('editable', String(editable));
    if (email) {
      body.append('email', email);
    }
    if (user) {
      body.append('user', user);
    }

    return await apiTryFetchRetry(`${url}/share/create`, {
      method: 'POST',
      body,
    });
  };

  const getInvites = async (
    take = 10,
    skip = 0,
    accepted: boolean
  ): Promise<
    | {
        results: Invite[];
        total: number;
        skip: number;
        take: number;
      }
    | { success: false; error: string }
  > => {
    return await apiTryFetchRetry(
      `${url}/invites/index?take=${take}&skip=${skip}${
        (accepted && `&accepted=true`) || ''
      }`,
      {
        method: 'GET',
      }
    );
  };

  const getActiveChallenges = async (
    take = 10,
    skip = 0,
    includeLimit = false
  ): Promise<{
    error?: string;
    success?: boolean;
    results?: Challenge[];
    nextCursor?: string;
    total?: number;
    skip?: number;
    take?: number;
  }> => {
    return await apiTryFetchRetry(
      `${url}/challenges/index?take=${take}&skip=${skip}&includeLimit=${includeLimit}&filter=live`,
      {
        method: 'GET',
      }
    );
  };

  const createEvent = async (
    event: string,
    content: JSONValue
  ): Promise<void> => {
    if (!getAccessToken()) {
      return;
    }
    await apiTryFetchRetry(`${url}/events/create`, {
      method: 'POST',
      body: JSON.stringify({ type: event, data: JSON.stringify(content) }),
    });

    return;
  };

  const getActions = async (): Promise<ActionsResponse> => {
    return await apiTryFetchRetry(`${url}/actions/index?addDates=true`, {
      method: 'GET',
    });
  };

  const createAction = async (id: string): Promise<ActionsResponse> => {
    return await apiTryFetchRetry(`${url}/actions/create?addDates=true`, {
      method: 'POST',
      body: JSON.stringify({ id }),
    });
  };

  const setGeneratedContentPublicMode = async (
    value: boolean
  ): Promise<ToggleGeneratedContentPublicModeResponse> => {
    return await apiTryFetchRetry<ToggleGeneratedContentPublicModeResponse>(
      `${url}/users/generated_content_public_mode`,
      {
        method: 'POST',
        body: JSON.stringify({
          value,
        }),
      }
    );
  };

  const requestConfirmationCode = async (): Promise<GenericApiResponse> => {
    return await apiTryFetchRetry<GenericApiResponse>(
      `${url}/users/request_confirmation_code`,
      {
        method: 'POST',
      }
    );
  };

  const requestEmailOtp = async ({
    powChallenge,
  }: {
    powChallenge?: PoWChallenge;
  }): Promise<
    GenericApiResponse<{
      canResendAt: string;
    }>
  > => {
    const resp = await apiFetchRetry(`${url}/users/email_verifications/init`, {
      method: 'POST',
      headers: {
        'x-challenge-nonce': powChallenge?.nonce ?? '',
        'x-challenge-solution': powChallenge?.solution?.toString() ?? '',
      },
    });

    const result = await resp.json();
    return result;
  };

  const verifyEmailOtp = async ({
    otp,
    powChallenge,
  }: {
    otp: string;
    powChallenge?: PoWChallenge;
  }): Promise<GenericApiResponse> => {
    const resp = await apiFetchRetry(`${url}/users/email_verifications/otp`, {
      method: 'POST',
      headers: {
        'x-challenge-nonce': powChallenge?.nonce ?? '',
        'x-challenge-solution': powChallenge?.solution?.toString() ?? '',
      },
      body: JSON.stringify({
        otp,
      }),
    });

    const result = await resp.json();
    return result;
  };

  const generatePowChallenge = async (): Promise<
    GenericApiResponse<PoWChallengeResponse>
  > => {
    return await apiTryFetchRetry<GenericApiResponse<PoWChallengeResponse>>(
      `${url}/auth/challenges/create`,
      {
        method: 'POST',
      }
    );
  };

  const createErasePaths = async (
    object: ErasePathObject[],
    designId: string
  ): Promise<GenericApiResponse<CreateUploadResponse>> => {
    let objectId: string;

    try {
      const file = new File([JSON.stringify(object)], '', {
        type: 'application/json',
      });
      objectId = await uploadObject(file, file.type);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      return { success: false, error: errorMessage };
    }

    const body = {
      objectIds: [objectId],
      designId,
    };

    return await apiTryFetchRetry<GenericApiResponse<CreateUploadResponse>>(
      `${url}/erasepaths/create`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );
  };

  const createIllustrationChildren = async (
    object: IllustrationSerializedChildren,
    designId: string,
    childrenId: string
  ): Promise<GenericApiResponse<CreateUploadResponse>> => {
    let objectId: string;

    try {
      const file = new File([JSON.stringify(object)], '', {
        type: 'application/json',
      });
      objectId = await uploadObject(file, file.type);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      return { success: false, error: errorMessage };
    }

    const body = {
      objectIds: [objectId],
      pathIds: [childrenId],
      designId,
      type: 'VECTOR_EDIT_PATH',
    };

    return await apiTryFetchRetry<GenericApiResponse<CreateUploadResponse>>(
      `${url}/paths/create`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );
  };

  const answerQuestionnaire = async ({
    questionKey,
    answerText,
  }: {
    questionKey: string;
    answerText: string;
  }): Promise<GenericApiResponse> => {
    return await apiTryFetchRetry<GenericApiResponse>(
      `${url}/users/answer_questionnaire`,
      {
        method: 'POST',
        body: JSON.stringify({ questionKey, answerText }),
      }
    );
  };

  const getUserAttribute = async ({
    attributeName,
  }: {
    attributeName: string;
  }): Promise<{ data: string } | null> => {
    const userId = getUserId();
    const attributeValue = await apiTryFetchRetry<{ data: string }>(
      `${url}/users/${userId}/attributes/${attributeName}`,
      {
        method: 'GET',
      }
    );
    if ('data' in attributeValue) {
      return attributeValue;
    }
    return null;
  };

  const createUpdateUserAttribute = async ({
    attributeName,
    value,
  }: {
    attributeName: string;
    value: string;
  }): Promise<GenericApiResponse> => {
    const userId = getUserId();
    return await apiTryFetchRetry<GenericApiResponse>(
      `${url}/users/${userId}/attributes/${attributeName}`,
      {
        method: 'PUT',
        body: JSON.stringify({
          value,
        }),
      }
    );
  };

  const claimOnboardingChecklistReward =
    async (): Promise<GenericApiResponse> => {
      return await apiTryFetchRetry<GenericApiResponse>(
        `${url}/users/claim_onboarding_checklist_reward`,
        {
          method: 'POST',
        }
      );
    };

  const resetOnboardingChecklist = async (): Promise<GenericApiResponse> => {
    return await apiTryFetchRetry<GenericApiResponse>(
      `${url}/users/reset_onboarding_checklist`,
      {
        method: 'POST',
      }
    );
  };

  const deleteAction = async (
    userId: string,
    actionId: string
  ): Promise<DeleteActionResponse> => {
    return await apiTryFetchRetry<DeleteActionResponse>(
      `${url}/actions/delete`,
      {
        method: 'POST',
        body: JSON.stringify({ userId, actionId }),
      }
    );
  };

  const resetOnboardingSurvey = async (): Promise<GenericApiResponse> => {
    return await apiTryFetchRetry<GenericApiResponse>(
      `${url}/users/reset_onboarding_survey`,
      {
        method: 'POST',
      }
    );
  };

  return {
    // authentication
    createUser,
    getUserInfo,
    getBasicUserInfo,
    getUserCreditInfo,
    getSSOToken,
    exchangeTemporaryToken,
    createSession,
    createTeamSession,
    deleteSession,
    requestPasswordResetCode,
    generatePowChallenge,

    // onboarding
    updateOnboardingState,
    answerQuestionnaire,

    // uploads
    getUploadedElements,
    uploadElement,
    uploadElements,
    uploadObject,
    uploadObjects,
    deleteUpload,
    deleteUploads,
    // designs
    getDesign,
    getDesigns,
    getLockedDesigns,
    getDesignsWithoutFolders,
    createDesign,
    updateDesign,
    deleteDesign,
    shareDesign,
    duplicateDesign,
    lockDesign,
    unlockDesign,
    removeAiImageDesign,

    // share
    getShare,

    getDesignOwner,

    // bookmarks
    getBookmarkReferences,
    getBookmarks,
    createBookmark,
    deleteBookmark,

    // notifications
    getNotifications,
    createCmsNotification,
    markNotificationRead,
    markNotificationsSeen,

    // user
    findUser,
    requestConfirmationCode,
    requestEmailOtp,
    verifyEmailOtp,
    updateUserEmail,

    // subscription
    changeSubscription,

    // posts
    createPost,
    updatePost,

    // invites
    getInvites,

    // challenges
    getActiveChallenges,

    // misc
    createEvent,
    setGeneratedContentPublicMode,

    // actions
    getActions,
    createAction,
    deleteAction,

    // erase paths
    createErasePaths,

    // attributes
    getUserAttribute,
    createUpdateUserAttribute,

    createIllustrationChildren,
    // onboarding checklist
    claimOnboardingChecklistReward,
    resetOnboardingChecklist,

    // onboarding survey
    resetOnboardingSurvey,
  };
};

export default createAPI({ url: USER_API_URL });
